import { RIYAL } from "@/lib/currency";
import { forwardRef, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Building2, FileText, User, Info, CreditCard } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/lib/formatters";
import type { BilledToInfo, InvoiceDocType, InvoiceDocumentCompany } from "@/types/database";
import { displayEmail, isPlaceholderEmail } from "@/lib/email";

/** A single row in the "البنود والخدمات" table. `description` may be rich (link + badge). */
export interface InvoiceDocumentLineItem {
  id: string;
  description: ReactNode;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface InvoiceDocumentProps {
  docType: InvoiceDocType;
  /** Document title shown in the brand-colored header. */
  title: { ar: string; en: string };
  /** Pill text in the header. Defaults to the simplified/standard label. */
  typeBadge?: { ar: string; en: string };
  /** Show the "standard tax invoice → commercial entity" amber alert. */
  showStandardAlert?: boolean;
  company: InvoiceDocumentCompany | null | undefined;
  /**
   * Overrides the party shown in the "Invoice From" block (NOT the footer).
   * Set by callers for owner-issued invoices (rent / sale / deposit) so the
   * tax document names the property OWNER as issuer; left undefined for every
   * other kind, where the block falls back to `company` (Dallali). The ZATCA QR
   * seller is resolved separately by the caller and deliberately stays `company`.
   */
  invoiceFrom?: InvoiceDocumentCompany | null;
  invoiceNumber: string | null;
  /** Pre-formatted issue date. */
  issueDate: ReactNode;
  /** Pre-formatted due date. */
  dueDate: ReactNode;
  status: { labelAr: string; labelEn: string; color: string };
  billedTo: BilledToInfo | null;
  /** Contract type of the invoice's contract (when known). Sale contracts
   *  render the billed party as بائع/مشتري (Seller/Buyer) instead of the
   *  lease مالك/مستأجر (Owner/Tenant). */
  contractType?: string | null;
  /** Right-hand card of row 2 — property/unit/contract for invoices, bundle facts for master. */
  detailsSlot?: ReactNode;
  lineItems: InvoiceDocumentLineItem[];
  /** Rendered as a single row when `lineItems` is empty (priced at `subtotal`). */
  fallbackDescription?: ReactNode;
  subtotal: number;
  /** `null` hides the "(x%)" suffix — used for mixed-rate master invoices. */
  vatRate: number | null;
  vatAmount: number;
  /** Show the residential-lease VAT-exemption badge. */
  showVatExempt?: boolean;
  discountAmount?: number;
  totalAmount: number;
  paidAmount?: number;
  balance?: number;
  qrData?: string | null;
  /** Renders the in-document Pay Now button when set (and status is payable). */
  payHref?: string | null;
  /** Linked-receipts section — invoices only. */
  receiptsSlot?: ReactNode;
  notes?: string | null;
  /** Extra no-print content rendered before the footer (e.g. a navigation button). */
  footerSlot?: ReactNode;
  /** Company line shown in the footer (defaults to `company`). */
  footerCompany?: InvoiceDocumentCompany | null;
  /** Optional test id on the header status pill. */
  statusTestId?: string;
  /** Optional test id on each line-item row. */
  lineItemTestId?: string;
  /** Optional test id on the grand-total amount (financial summary footer). */
  totalTestId?: string;
}

/**
 * Presentational tax-invoice document body (brand header → from/to → line items
 * → totals → QR/pay → receipts → footer). Extracted from `InvoiceDetailsModal`
 * so both single invoices and bundled "master" invoices render through the same
 * branded layout. Pure presentation — all data is resolved by the caller.
 *
 * The forwarded ref attaches to the print area so callers can drive PDF export.
 */
export const InvoiceDocument = forwardRef<HTMLDivElement, InvoiceDocumentProps>(
  function InvoiceDocument(
    {
      docType,
      title,
      typeBadge,
      showStandardAlert,
      company,
      invoiceFrom,
      invoiceNumber,
      issueDate,
      dueDate,
      status,
      billedTo,
      contractType,
      detailsSlot,
      lineItems,
      fallbackDescription,
      subtotal,
      vatRate,
      vatAmount,
      showVatExempt,
      discountAmount = 0,
      totalAmount,
      paidAmount = 0,
      balance = 0,
      qrData,
      payHref,
      receiptsSlot,
      notes,
      footerSlot,
      footerCompany,
      statusTestId,
      lineItemTestId,
      totalTestId,
    },
    ref,
  ) {
    const { t } = useLanguage();
    const footer = footerCompany ?? company;
    // Owner-issued invoices (rent/sale/deposit) pass `invoiceFrom`; everything
    // else falls back to the company. The footer is intentionally unaffected.
    const from = invoiceFrom ?? company;
    const isOwnerIssued = invoiceFrom != null;
    const badge =
      typeBadge ??
      (docType === "standard"
        ? { ar: "ضريبية | Standard", en: "Standard | ضريبية" }
        : { ar: "مبسطة | Simplified", en: "Simplified | مبسطة" });

    return (
      <div ref={ref} className="invoice-print-area">
        {/* ─── Brand Header (follows company brand color via --primary) ─── */}
        <div className="bg-gradient-to-l from-primary to-primary/85 text-white px-8 py-6 print:px-6 print:py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/dallali-logo.png"
              alt="Dallali Logo"
              className="h-12 print:h-10 w-auto object-contain brightness-0 invert"
            />
          </div>
          <div className="text-end">
            <h2 className="text-xl print:text-lg font-bold">{title.ar}</h2>
            <p className="text-sm opacity-80 mt-0.5">{title.en}</p>
            <span className="inline-block mt-2 print:mt-1 bg-white/20 border border-white/40 rounded-full px-4 py-1 print:py-0.5 text-xs font-medium">
              {t(badge.ar, badge.en)}
            </span>
          </div>
        </div>

        {/* ─── Standard Tax Invoice Alert ─── */}
        {showStandardAlert && (
          <div className="mx-8 mt-4 print:mx-6 print:mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 print:py-2">
            <Info className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              {t(
                "هذه فاتورة ضريبية كاملة صادرة لمنشأة تجارية — تتضمن بيانات المشتري الضريبية",
                "This is a standard tax invoice issued to a commercial entity — includes buyer tax details",
              )}
            </p>
          </div>
        )}

        {/* ─── Body ───
            print:* spacing keeps a typical invoice on one A4 page in print media
            (the server PDF, pdf-renderer.service.ts, and Ctrl+P both print it).
            The footer's print:-mx/-mb must match this print:px/py. Check page
            counts with tools/e2e/invoice-pdf-pages/run.mjs. */}
        <div className="px-8 py-6 space-y-6 print:px-6 print:py-4 print:space-y-3">
          {/* Row 1: Invoice From + Invoice Info.
              print:grid-cols-2 — mobile print viewports sit below the md
              breakpoint, which used to stack every card and push the invoice
              onto a second page. Print is always two-up. */}
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-3">
            {/* Invoice From */}
            <div className="bg-gray-50 rounded-xl p-5 print:p-3">
              <h3 className="text-sm font-semibold text-primary mb-3 print:mb-2 flex items-center gap-2">
                <span className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                  <Building2 className="h-2.5 w-2.5 text-white" />
                </span>
                {t("الفاتورة من", "Invoice From")}
                {isOwnerIssued && (
                  <Badge variant="outline" className="text-[10px] px-2 py-0">{t("مالك", "Owner")}</Badge>
                )}
              </h3>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-gray-800">{from?.name || "شركة دلّالي"}</p>
                {from?.name_en && <p className="text-gray-500 text-xs">{from.name_en}</p>}
                {from?.cr_number && (
                  <p className="text-gray-400 text-xs mt-2">{t("س.ت", "CR")} | {from.cr_number}</p>
                )}
                {from?.vat_number && (
                  <p className="text-gray-400 text-xs">{t("الرقم الضريبي", "VAT No.")} | {from.vat_number}</p>
                )}
                {from?.address && <p className="text-gray-400 text-xs">{from.address}</p>}
              </div>
            </div>

            {/* Invoice Info */}
            <div className="bg-gray-50 rounded-xl p-5 print:p-3">
              <h3 className="text-sm font-semibold text-primary mb-3 print:mb-2 flex items-center gap-2">
                <span className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                  <FileText className="h-2.5 w-2.5 text-white" />
                </span>
                {t("بيانات الفاتورة", "Invoice Info")}
              </h3>
              <div className="space-y-2">
                {invoiceNumber && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t("رقم الفاتورة", "Invoice No.")}</span>
                    <span className="font-medium text-gray-700">{invoiceNumber}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("تاريخ الإصدار", "Issue Date")}</span>
                  <span className="font-medium text-gray-700">{issueDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("تاريخ الاستحقاق", "Due Date")}</span>
                  <span className="font-medium text-gray-700">{dueDate}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-400">{t("الحالة", "Status")}</span>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}
                    data-testid={statusTestId}
                  >
                    {t(status.labelAr, status.labelEn)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Billed To + Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-3">
            {/* Billed To */}
            <div className="bg-gray-50 rounded-xl p-5 print:p-3">
              <h3 className="text-sm font-semibold text-primary mb-3 print:mb-2 flex items-center gap-2">
                <span className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                  <User className="h-2.5 w-2.5 text-white" />
                </span>
                {t("الفاتورة إلى", "Billed To")}
                {billedTo?.type === "owner" && (
                  <Badge variant="outline" className="text-[10px] px-2 py-0">
                    {contractType === "sale" ? t("بائع", "Seller") : t("مالك", "Owner")}
                  </Badge>
                )}
                {billedTo?.type === "tenant" && (
                  <Badge variant="outline" className="text-[10px] px-2 py-0">
                    {contractType === "sale" ? t("مشتري", "Buyer") : t("مستأجر", "Tenant")}
                  </Badge>
                )}
              </h3>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-gray-800">{billedTo?.name || "---"}</p>
                {billedTo?.nameEn && billedTo.nameEn !== billedTo.name && (
                  <p className="text-gray-500 text-xs">{billedTo.nameEn}</p>
                )}
                {billedTo?.phone && <p className="text-gray-400 text-xs mt-2">{billedTo.phone}</p>}
                {!isPlaceholderEmail(billedTo?.email) && <p className="text-gray-400 text-xs">{displayEmail(billedTo?.email)}</p>}
                {billedTo?.address && <p className="text-gray-400 text-xs">{billedTo.address}</p>}
                {/* Standard Invoice: CR & ID */}
                {docType === "standard" && billedTo?.crNumber && (
                  <p className="text-gray-500 text-xs font-medium mt-2">
                    {t("س.ت", "CR No.")} | {billedTo.crNumber}
                  </p>
                )}
                {docType === "standard" && billedTo?.vatNumber && (
                  <p className="text-gray-500 text-xs font-medium">
                    {t("الرقم الضريبي", "VAT No.")} | {billedTo.vatNumber}
                  </p>
                )}
                {docType === "standard" && billedTo?.idNumber && (
                  <p className="text-gray-400 text-xs">
                    {billedTo.idType === "national_id" ? t("هوية وطنية", "National ID") :
                      billedTo.idType === "iqama" ? t("إقامة", "Iqama") :
                        billedTo.idType === "passport" ? t("جواز سفر", "Passport") :
                          t("رقم الهوية", "ID No.")} | {billedTo.idNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="bg-gray-50 rounded-xl p-5 print:p-3">
              <h3 className="text-sm font-semibold text-primary mb-3 print:mb-2 flex items-center gap-2">
                <span className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                  <FileText className="h-2.5 w-2.5 text-white" />
                </span>
                {t("التفاصيل", "Details")}
              </h3>
              <div className="space-y-2">{detailsSlot}</div>
            </div>
          </div>

          {/* ─── Separator ─── */}
          <Separator />

          {/* ─── Items Table ─── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 print:mb-2">
              {t("البنود والخدمات", "Services & Items")}
            </h3>
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="text-xs font-semibold text-gray-500">#</TableHead>
                    <TableHead className="w-[50%] text-xs font-semibold text-gray-500">{t("الوصف", "Description")}</TableHead>
                    <TableHead className="text-center text-xs font-semibold text-gray-500">{t("الكمية", "Qty")}</TableHead>
                    <TableHead className="text-end text-xs font-semibold text-gray-500">{t("سعر الوحدة", "Unit Price")}</TableHead>
                    <TableHead className="text-end text-xs font-semibold text-gray-500">{t("الإجمالي", "Total")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, idx) => (
                      <TableRow key={item.id} data-testid={lineItemTestId}>
                        <TableCell className="text-sm text-gray-500">{idx + 1}</TableCell>
                        <TableCell className="text-sm text-gray-700">{item.description}</TableCell>
                        <TableCell className="text-center text-sm text-gray-700">{item.quantity || 1}</TableCell>
                        <TableCell className="text-end text-sm text-gray-700">{formatNumber(item.unitPrice)}&nbsp;{RIYAL}</TableCell>
                        <TableCell className="text-end text-sm font-medium text-gray-800">{formatNumber(item.total)}&nbsp;{RIYAL}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell className="text-sm text-gray-500">1</TableCell>
                      <TableCell className="text-sm text-gray-700">{fallbackDescription || t("قيمة الإيجار", "Rent amount")}</TableCell>
                      <TableCell className="text-center text-sm text-gray-700">1</TableCell>
                      <TableCell className="text-end text-sm text-gray-700">{formatNumber(subtotal)}&nbsp;{RIYAL}</TableCell>
                      <TableCell className="text-end text-sm font-medium text-gray-800">{formatNumber(subtotal)}&nbsp;{RIYAL}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Print: summary and QR side by side — stacked they cost ~100px, which
              pushes an invoice with linked receipts onto a second A4 page. */}
          <div className="space-y-6 print:space-y-0 print:flex print:items-stretch print:gap-3">
            {/* ─── Financial Summary ─── */}
            <div className="bg-gray-50 rounded-xl p-5 print:p-3 print:flex-1">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("المجموع الفرعي", "Subtotal")}</span>
                  <span className="font-medium text-gray-700">{formatNumber(subtotal)}&nbsp;{RIYAL}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <div className="flex flex-col">
                    <span className="text-gray-500">
                      {t("ضريبة القيمة المضافة", "VAT")}{vatRate !== null ? ` (${vatRate}%)` : ""}
                    </span>
                    {showVatExempt && (
                      <span className="mt-0.5 self-start px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {t("معفى — إيجار سكني", "Exempt — residential lease")}
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-gray-700">{formatNumber(vatAmount || 0)}&nbsp;{RIYAL}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t("الخصم", "Discount")}</span>
                    <span className="text-red-600 font-medium">-{formatNumber(discountAmount)}&nbsp;{RIYAL}</span>
                  </div>
                )}
                <div className="border-t-2 border-primary pt-3 mt-2">
                  <div className="flex justify-between">
                    <span className="text-base font-bold text-primary">{t("الإجمالي", "Total")}</span>
                    <span className="text-base font-bold text-primary" data-testid={totalTestId}>{formatNumber(totalAmount)}&nbsp;{RIYAL}</span>
                  </div>
                </div>
                {paidAmount > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">{t("المدفوع", "Paid")}</span>
                    <span className="text-green-600 font-medium">{formatNumber(paidAmount)}&nbsp;{RIYAL}</span>
                  </div>
                )}
                {balance > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t("المتبقي", "Balance")}</span>
                    <span className="text-red-600 font-bold">{formatNumber(balance)}&nbsp;{RIYAL}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ─── QR Code + Pay Button ─── */}
            <div className="flex flex-col md:flex-row print:flex-row print:flex-1 print:empty:hidden items-center gap-4">
              {/* ZATCA QR */}
              {qrData && (
                <div className="bg-gray-50 rounded-xl p-4 print:p-3 flex items-center gap-4 flex-1">
                  <div className="p-2 print:p-1 bg-white rounded-lg border shrink-0 print:[&_svg]:h-16 print:[&_svg]:w-16">
                    <QRCodeSVG value={qrData} size={70} level="M" />
                  </div>
                  <div className="text-xs text-gray-400 leading-relaxed">
                    <p className="font-semibold text-gray-600 mb-1">ZATCA E-Invoice QR Code</p>
                    <p>{t("رمز الاستجابة السريعة للفاتورة الإلكترونية", "Electronic invoice QR code")}</p>
                    <p>{t("متوافق مع هيئة الزكاة والضريبة والجمارك", "Compliant with ZATCA regulations")}</p>
                  </div>
                </div>
              )}

              {/* Pay Button */}
              {payHref && (
                <div className="flex-1 flex justify-center">
                  <a
                    href={payHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pay-invoice-btn inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-white font-semibold text-sm w-full max-w-xs py-3 hover:brightness-90 transition-[filter] no-underline shadow-lg"
                  >
                    <CreditCard className="h-4 w-4" />
                    {t("ادفع الآن", "Pay Now")}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* ─── Linked Receipts ─── */}
          {receiptsSlot}

          {/* ─── Notes ─── */}
          {notes && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{t("ملاحظات", "Notes")}</h3>
                <p className="text-sm text-gray-500 whitespace-pre-wrap">{notes}</p>
              </div>
            </>
          )}

          {/* ─── Extra (navigation) ─── */}
          {footerSlot}

          {/* ─── Footer ─── */}
          <div className="bg-gray-50 border-t text-center py-3 text-[11px] text-gray-400 rounded-b-xl -mx-8 -mb-6 px-8 mt-4 print:-mx-6 print:-mb-4 print:px-6 print:mt-2 print:py-2 print:break-inside-avoid">
            {footer?.name || "شركة الدلال الجديد لتقنية المعلومات"} | {footer?.name_en || "AlDallal AlJadeed for IT Co."} | {t("س.ت", "CR")} {footer?.cr_number || "1010685632"} | {t("دلّالي", "Dallali")}
          </div>
        </div>
      </div>
    );
  },
);
